import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Check, X, Mail, Clock } from "lucide-react";

function relative(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return "upravo sad";
  if (s < 3600) return `pre ${Math.floor(s / 60)} min`;
  if (s < 86400) return `pre ${Math.floor(s / 3600)} h`;
  const days = Math.floor(s / 86400);
  if (days < 7) return `pre ${days} d`;
  return d.toLocaleDateString("sr-RS");
}

function fmtDateTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("sr-RS", { dateStyle: "medium", timeStyle: "short" });
}

export default function PendingRegistrations() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // uid currently being acted on

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/users");
      setUsers((data || []).filter((u) => u.status === "pending"));
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const approve = async (uid) => {
    setBusy(uid);
    try {
      await api.post(`/users/${uid}/approve`);
      toast.success("Korisnik odobren");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const reject = async (uid) => {
    setBusy(uid);
    try {
      await api.post(`/users/${uid}/reject`);
      toast.success("Zahtev odbijen");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="pending-registrations-page">
      <header className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-ink flex items-center gap-2">
          <UserPlus className="w-6 h-6 text-forest" />
          Zahtevi za registraciju
        </h1>
        <p className="text-sm text-ink-muted mt-1">
          Osobe koje čekaju tvoje odobrenje da uđu u aplikaciju.
        </p>
      </header>

      {loading ? (
        <div className="text-sm text-ink-muted flex items-center gap-2" data-testid="pending-loading">
          <span className="w-3 h-3 border-2 border-ink-muted/30 border-t-forest rounded-full animate-spin" />
          Učitavam zahteve…
        </div>
      ) : error ? (
        <div
          data-testid="pending-error"
          className="border border-terracotta/30 bg-terracotta/6 text-terracotta text-sm rounded-md px-3 py-2 flex justify-between"
        >
          <span>{error}</span>
          <button onClick={load} className="font-medium hover:underline">Osveži</button>
        </div>
      ) : users.length === 0 ? (
        <div
          data-testid="pending-empty"
          className="border border-dashed border-line rounded-md p-8 text-center text-sm text-ink-muted"
        >
          Nema zahteva za odobrenje.
        </div>
      ) : (
        <div className="space-y-2" data-testid="pending-list">
          {users.map((u) => (
            <div
              key={u.id}
              data-testid={`pending-row-${u.id}`}
              className="border border-line rounded-md bg-white p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-ink" data-testid={`pending-name-${u.id}`}>
                  {u.name || "(bez imena)"}
                </div>
                <div className="text-xs text-ink-muted flex flex-wrap gap-x-3 gap-y-1 mt-1">
                  <span className="inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {u.email}
                  </span>
                  <span className="inline-flex items-center gap-1" title={fmtDateTime(u.created_at)}>
                    <Clock className="w-3 h-3" /> Prijavljen {fmtDateTime(u.created_at)} · čeka {relative(u.created_at)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => approve(u.id)}
                  disabled={busy === u.id}
                  data-testid={`pending-approve-${u.id}`}
                  className="inline-flex items-center gap-1 bg-forest text-white text-xs px-3 py-1.5 rounded-md hover:bg-forest/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" /> Odobri
                </button>
                <button
                  onClick={() => reject(u.id)}
                  disabled={busy === u.id}
                  data-testid={`pending-reject-${u.id}`}
                  className="inline-flex items-center gap-1 border border-terracotta/40 text-terracotta text-xs px-3 py-1.5 rounded-md hover:bg-terracotta/8 disabled:opacity-60"
                >
                  <X className="w-3.5 h-3.5" /> Odbij
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
